import type { JSX } from 'react';
import {
  Node,
  mergeAttributes,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';
import { useRef } from 'react';
import {
  DeleteOutlined,
  LinkOutlined,
  UploadOutlined,
  PictureOutlined,
} from '@ant-design/icons';

/**
 * ImageGallery — блок-галерея: несколько картинок в ряд (2–5 колонок).
 * На десктопе — сетка N колонок; на узких экранах CSS делает горизонтальную
 * прокрутку со snap (как карусель). Стили — .fk-gallery в global.css/editor.css
 * (одинаковы в редакторе и читалке).
 *
 * Round-trip в .md: сериализуется в fenced-блок
 *   ```gallery 4
 *   /api/wiki/assets/a.png
 *   /api/wiki/assets/b.png
 *   ```
 * Парсинг ```gallery → grid из <img> делается в markdown-html.ts (редактор)
 * и в backend wiki-markdown.service.ts (читалка) — держать синхронно.
 */

/** Та же сигнатура, что у MediaEmbed/SlashCommands: вернуть URL или null. */
export type GalleryUploadFn = (
  file: File,
) => Promise<{ url: string; fileName: string; kind: string } | null>;

export interface ImageGalleryOptions {
  uploadFile?: GalleryUploadFn;
}

export const ImageGallery = Node.create<ImageGalleryOptions>({
  name: 'imageGallery',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addOptions() {
    return { uploadFile: undefined };
  },

  addAttributes() {
    return {
      // Список URL картинок. Рендерятся детьми в renderHTML ноды (см. ниже),
      // поэтому на уровне атрибута в DOM не пишем.
      images: {
        default: [] as string[],
        parseHTML: (el: HTMLElement) =>
          Array.from(el.querySelectorAll('img'))
            .map((i) => i.getAttribute('src') || '')
            .filter(Boolean),
        renderHTML: () => ({}),
      },
      cols: {
        default: 3,
        parseHTML: (el: HTMLElement) => parseInt(el.getAttribute('data-cols') || '3', 10) || 3,
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gallery]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const cols = Number(node.attrs.cols) || 3;
    const images: string[] = Array.isArray(node.attrs.images) ? node.attrs.images : [];
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-gallery': '',
        'data-cols': String(cols),
        class: 'fk-gallery',
        style: `--fk-gal-cols:${cols}`,
      }),
      ...images.map((src) => ['img', { src, loading: 'lazy', alt: '' }]),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(GalleryView);
  },
});

function GalleryView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode, selected, extension } = props;
  const images: string[] = Array.isArray(node.attrs.images) ? node.attrs.images : [];
  const cols: number = Number(node.attrs.cols) || 3;
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadFile: GalleryUploadFn | undefined = extension.options.uploadFile;

  const setImages = (next: string[]) => updateAttributes({ images: next });

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || !uploadFile) return;
    const urls: string[] = [];
    for (const f of Array.from(files)) {
      try {
        const r = await uploadFile(f);
        if (r?.url) urls.push(r.url);
      } catch {
        /* пропускаем сбойный файл */
      }
    }
    if (urls.length) setImages([...images, ...urls]);
  };

  const addByUrl = () => {
    const url = window.prompt('URL картинки (можно несколько через запятую)');
    if (!url) return;
    const parts = url
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length) setImages([...images, ...parts]);
  };

  const removeAt = (idx: number) => setImages(images.filter((_, i) => i !== idx));

  return (
    <NodeViewWrapper className="fk-gallery-edit" style={{ margin: '14px 0' }}>
      <div
        contentEditable={false}
        style={{
          border: selected ? '2px solid var(--fk-orange)' : '1px dashed var(--fk-border, #e6e6ea)',
          borderRadius: 10,
          padding: 10,
          background: '#fff',
        }}
      >
        {/* Тулбар: колонки + добавить + удалить блок */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: images.length ? 10 : 0,
            flexWrap: 'wrap',
          }}
        >
          <PictureOutlined style={{ color: 'var(--fk-orange)' }} />
          <span style={{ fontSize: 13, color: 'var(--fk-text-2, #6b6b76)' }}>Галерея</span>
          <span style={{ fontSize: 12, color: '#9b97ad' }}>колонок:</span>
          {[2, 3, 4, 5].map((c) => (
            <button
              key={c}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                updateAttributes({ cols: c });
              }}
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                cursor: 'pointer',
                border: c === cols ? '1.5px solid var(--fk-orange)' : '1px solid var(--fk-border,#e6e6ea)',
                background: c === cols ? 'var(--fk-orange-soft, #fff3e8)' : '#fff',
                color: c === cols ? 'var(--fk-orange)' : '#555',
                fontWeight: 600,
              }}
            >
              {c}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          {uploadFile && (
            <button type="button" className="fk-gal-btn" onMouseDown={(e) => { e.preventDefault(); fileRef.current?.click(); }}>
              <UploadOutlined /> С компьютера
            </button>
          )}
          <button type="button" className="fk-gal-btn" onMouseDown={(e) => { e.preventDefault(); addByUrl(); }}>
            <LinkOutlined /> По URL
          </button>
          <button type="button" className="fk-gal-btn" data-danger onMouseDown={(e) => { e.preventDefault(); deleteNode(); }}>
            <DeleteOutlined />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => void onPickFiles(e.target.files)}
          />
        </div>

        {images.length === 0 ? (
          <div style={{ padding: '18px 8px', textAlign: 'center', color: '#aaa', fontSize: 13 }}>
            Пусто. Добавь картинки — «С компьютера» или «По URL».
          </div>
        ) : (
          <div
            className="fk-gallery"
            data-cols={cols}
            style={{ ['--fk-gal-cols' as string]: cols }}
          >
            {images.map((src, idx) => (
              <div key={`${src}-${idx}`} className="fk-gallery-cell">
                <img src={src} alt="" />
                <button
                  type="button"
                  className="fk-gal-del"
                  title="Убрать"
                  onMouseDown={(e) => { e.preventDefault(); removeAt(idx); }}
                >
                  <DeleteOutlined />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        .fk-gallery-edit .fk-gal-btn{display:inline-flex;align-items:center;gap:5px;border:1px solid var(--fk-border,#e6e6ea);background:#fff;border-radius:7px;padding:3px 9px;font-size:12px;color:#444;cursor:pointer}
        .fk-gallery-edit .fk-gal-btn:hover{background:var(--fk-bg-subtle,#f6f6f8)}
        .fk-gallery-edit .fk-gal-btn[data-danger]:hover{background:rgba(229,57,53,.1);color:var(--fk-error,#e53935)}
        .fk-gallery-edit .fk-gallery-cell{position:relative}
        .fk-gallery-edit .fk-gal-del{position:absolute;top:6px;right:6px;width:26px;height:26px;border:none;border-radius:7px;background:rgba(0,0,0,.55);color:#fff;cursor:pointer;display:none;align-items:center;justify-content:center}
        .fk-gallery-edit .fk-gallery-cell:hover .fk-gal-del{display:inline-flex}
      `}</style>
    </NodeViewWrapper>
  );
}
